/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useCallback, useState } from "react"

import { TTRDeviceFormData } from "../../../../../components/devices/ttr/types/providerTypes"
import { SUPPORT_REFER } from "../../../../../errors/errorMessages"
import { AppError } from "../../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../../errors/recognisedErrors"
import { snackbar } from "../../../../../utils/general/generalUtils"
import { getTypeTestPostBodyFromFormData } from "../../../../../utils/registerDevice/registerDeviceUtils"
import { postClientProtected } from "../../../clients/clients"
import {
    TPublishDevicePostBody,
    TPublishDevicePostBodyPartial,
} from "../../../types/devices/ttr/publishDeviceTypes"
import { PUBLISH_DEVICE } from "../../../urls/private"

interface IPublishDevice {
    publishDevice: () => Promise<void>
    isLoading: boolean
    isLoaded: boolean
    error?: AppError
}

/** Creates a POST body for a publish device request */
const createPostBodyFromFormData = (
    formData: TTRDeviceFormData,
    deviceId: string,
    deviceRef: string
): TPublishDevicePostBody => {
    const partialPostBody: TPublishDevicePostBodyPartial = {
        ManufacturerID: formData.coreDetails.manufacturer ?? undefined,
        DeviceID: deviceId.toString(),
        DeviceRef: deviceRef,
        ModelNo: formData.coreDetails.model,
        DeviceCategoryID: formData.coreDetails.deviceCategory!,
        DeviceTypeID: formData.coreDetails.deviceType!,
        RegisteredCapacity: formData.coreDetails.registeredCapacity! as number,
        SoftwareVersion: formData.coreDetails.softwareVersion,
        HardwareVersion: formData.coreDetails.hardwareVersion,
        DeviceComments: formData.coreDetails.additionalComments,
        PhaseID: formData.coreDetails.numberOfPhases!,
    }

    const typeTestPostBody = getTypeTestPostBodyFromFormData(formData)

    return {
        ...partialPostBody,
        ...typeTestPostBody,
    }
}

/** Returns a function that sends a POST request to create a device based on the provided formdata.
 * Also returns the state of the POST request: isLoading, isLoaded, error.
 */
const usePublishDevice = (
    formData: TTRDeviceFormData,
    deviceId?: string,
    deviceRef?: string
): IPublishDevice => {
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [error, setError] = useState<AppError | undefined>(undefined)
    const [isLoaded, setIsLoaded] = useState<boolean>(false)

    const publishDevice = useCallback(async (): Promise<void> => {
        if (!deviceId || !deviceRef) {
            return
        }

        const postBody: Record<string, string | number> =
            createPostBodyFromFormData(formData, deviceId, deviceRef)

        try {
            setIsLoading(true)
            await postClientProtected(PUBLISH_DEVICE, postBody)
            snackbar("Successfully published device", "success")
            setIsLoaded(true)
        } catch {
            const appError = invalidApiDataError("Couldn't publish Device")
            setError(appError)
            snackbar(
                `Couldn't publish device, please try again. ${SUPPORT_REFER}`,
                "warning"
            )
        } finally {
            setIsLoading(false)
        }
    }, [deviceId, deviceRef, formData])

    return {
        publishDevice,
        isLoading,
        isLoaded,
        error,
    }
}

export default usePublishDevice
